import { todayUtcIso, type UpcomingLockPeriod } from '@clockwise/shared';

/**
 * Closing-preview helpers for the timesheet settings page, split out so the
 * "which period does each boundary's next deadline belong to" rule is unit
 * testable without mounting the route — the same reason
 * `approvals-reject.ts` exists.
 */

/** One row of the "Next lock date" block: a period, and whichever of the two
 *  deadlines is still ahead on it. A row always carries at least one. */
export interface NextDeadlineRow {
  period: UpcomingLockPeriod;
  /** The submission deadline, when this period's is still ahead. */
  submission: string | null;
  /** The settlement deadline (hard close), when this period's is still ahead. */
  settlement: string | null;
}

/**
 * Each boundary's next deadline, on the period it actually belongs to.
 *
 * Usually one row — both fall on the same period. But a period stops accepting
 * new time before it finishes settling, so once `settleDays` pushes the
 * settlement window past a whole period, the earliest period still settling is
 * one whose submission deadline went by some time ago, and the two deadlines
 * sit on DIFFERENT periods.
 *
 * Each boundary is therefore resolved independently, by scanning for the first
 * period whose deadline for it is still ahead. An earlier version assumed the
 * answer was row 0 or row 1 and, at `settleDays >= 6` weekly — where more than
 * one consecutive period can be closed to submission but still settling —
 * showed no submission deadline at all, plus a row with an em-dash in both
 * columns.
 *
 * The settlement window contains the submission one, so a period still open to
 * submission is always still settling: the settlement row can only be at or
 * before the submission row, and pushing it first keeps the rows in
 * chronological order.
 *
 * Compared against UTC today because `upcomingLockDates` selected the periods
 * against the same clock. Enforcement uses the owner's local today, which can
 * differ by a day at the margin — the pre-existing, deliberate imprecision of
 * a tenant-wide preview, not a new one.
 */
export function nextDeadlineRows(
  schedule: readonly UpcomingLockPeriod[],
  asOf: string = todayUtcIso(),
): NextDeadlineRow[] {
  const settlementPeriod = schedule.find((p) => p.settleDate > asOf);
  const submissionPeriod = schedule.find((p) => p.lockDate > asOf);

  const rows: NextDeadlineRow[] = [];
  if (settlementPeriod) {
    rows.push({
      period: settlementPeriod,
      // Only when the same period carries both — otherwise it belongs on its
      // own row below, and claiming it here would print a date that has passed.
      submission: submissionPeriod === settlementPeriod ? settlementPeriod.lockDate : null,
      settlement: settlementPeriod.settleDate,
    });
  }
  if (submissionPeriod && submissionPeriod !== settlementPeriod) {
    rows.push({
      period: submissionPeriod,
      submission: submissionPeriod.lockDate,
      settlement: null,
    });
  }
  return rows;
}
